import { useTenantMemberships } from "@/contexts/TenantContext";
import { LazoPaths } from "@/enums/LazoPaths";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Workspace } from "@/models/LazoWorkspace";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import WorkspaceService from "@/services/workspaces/WorkspaceService";
import { getLandingPageByUser } from "@/utils/NavigationUtils";
import { redirectToWorkspace } from "@/utils/RedirectUtils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ChatParams } from "../components/communications-chat-components/ChatContainer";

export function useTenantSelect() {
  const { t } = useTranslation("navigation");
  const navigate = useNavigate();

  const { workspaceState, setSelectedTenantMembership } = useTenantMemberships();

  const [ownedWorkspaces, setOwnedWorkspaces] = useState<Workspace[]>([]);

  const items = workspaceState.workspaces.map((w) => {
    return {
      workspaceSlug: w.workspace.slug,
      tenants: (w?.memberships ?? []).map((m) => m?.tenant).filter((t) => !!t?.externalId),
    };
  });

  const tenantMemberships =
    workspaceState.workspaces.reduce((a, w) => {
      a.push(...(w?.memberships ?? []).filter((m) => !!m?.tenant?.externalId));
      return a;
    }, []) || [];

  const currentTenantMembership = workspaceState.selectedTenantMembership;

  useEffect(() => {
    WorkspaceService.getOwnedWorkspaces().then((r) => r.onSuccess(setOwnedWorkspaces));
  }, []);

  const hasPermissionsToAddCompany = AuthService.hasPermissionFor("Lazo:Portal:Access");

  function handleChange(tenantExternalId: string) {
    const workspaceSlug = items.find((i) => i.tenants.some((t) => t.externalId === tenantExternalId))?.workspaceSlug;
    const loggedUser: User = AuthService.getUser();
    const homePath = getLandingPageByUser(loggedUser) || LazoPaths.INDEX;

    if (workspaceState.selectedWorkspace.workspace.slug === workspaceSlug) {
      setSelectedTenantMembership(tenantMemberships.find((tm) => tenantExternalId === tm.tenant.externalId)!);
      navigate(homePath);
      return;
    }

    redirectToWorkspace(workspaceSlug, tenantExternalId, homePath);
  }

  function goToChat() {
    const loggedUser: User = AuthService.getUser();
    LazoChatConversationService.getConversationsWithKam(loggedUser.externalId, currentTenantMembership.tenant.externalId).then((r) => {
      r.onSuccess((c) => {
        if (c.length === 0) {
          LazoChatConversationService.createChatWithKam(currentTenantMembership.tenant.externalId).then((r) => {
            r.onSuccess((conv) =>
              navigate(LazoPaths.ONE_COMMUNICATIONS.replace(ChatParams.LazoChatTeamExternalId, conv.externalId))
            ).onError((e) => NotificationService.error(t("tenantSelect.errorCreatingKamConversation") + e));
          });
        } else {
          navigate(LazoPaths.ONE_COMMUNICATIONS.replace(ChatParams.LazoChatTeamExternalId, c[0].externalId));
        }
      }).onError((_) => NotificationService.error(t("tenantSelect.errorStartingChat")));
    });
  }

  function onClickAddWorkSpace() {
    if (hasPermissionsToAddCompany) {
      if (ownedWorkspaces.length === 0) {
        navigate(LazoPaths.ONE_SIGNUP);
      }
    }
  }

  return {
    workspaceState,
    items,
    tenantMemberships,
    currentTenantMembership,
    ownedWorkspaces,
    hasPermissionsToAddCompany,
    handleChange,
    goToChat,
    onClickAddWorkSpace,
  };
}
